<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TabsBar, { type TabItem } from '@/components/ui/TabsBar.vue';
import FavoritesOnboardingSheet from './FavoritesOnboardingSheet.vue';
import RoutesList from '@/pages/RoutesPage/index.vue';
import StopsList from '@/pages/StopsPage/index.vue';
import { shouldShowFavoritesOnboarding } from '@/libs/helpers/favorites-onboarding';
import { useAppStore } from '@/stores/app';
import { useGroupStore } from '@/stores/group';

// keep-alive in App.vue matches on this name so the active sub-tab and the
// scroll positions survive a round-trip to a detail view.
defineOptions({ name: 'SchedulesPage' });

const { t } = useI18n();
const appStore = useAppStore();
const groupStore = useGroupStore();

const activeTab = ref<'routes' | 'stops'>('routes');

const tabs = computed<TabItem[]>(() => [
  { id: 'routes', label: t('home.tabs.routes') },
  { id: 'stops', label: t('home.tabs.stops') },
]);

const activeIndex = computed(() => tabs.value.findIndex(({ id }) => id === activeTab.value));

// Decided once, on the first mount only (the page is kept alive, so a
// round-trip to a detail view never re-asks), and only once the network's
// favorites are known: they load with the network, before the loading screen
// lifts, and a deep link mounts this page before that.
const showFavoritesOnboarding = ref<boolean>(false);
let favoritesOnboardingDecided = false;

watch(
  () => groupStore.group !== null && !appStore.isLoadingAsBoolean,
  ready => {
    if (!ready || favoritesOnboardingDecided) return;
    favoritesOnboardingDecided = true;
    const hasFavorites =
      Object.keys(appStore.favorites.routes ?? {}).length > 0 ||
      Object.keys(appStore.favorites.onDemandRoutes ?? {}).length > 0 ||
      Object.keys(appStore.favorites.stops ?? {}).length > 0;
    showFavoritesOnboarding.value = shouldShowFavoritesOnboarding(hasFavorites);
  },
  { immediate: true },
);
</script>

<template>
  <div class="page" :class="{ 'page--no-navbar': appStore.shouldHideNavbar }">
    <div class="content schedules-page">
      <h1 class="schedules-page__title">{{ $t('nav.schedules') }}</h1>

      <TabsBar v-model="activeTab" class="schedules-page__tabs" :tabs="tabs" />

      <!-- Both lists stay mounted and slide as one track, so each sub-tab keeps
           its own search text, incremental loading and scroll position. -->
      <div class="schedules-page__viewport">
        <div class="schedules-page__track" :style="{ transform: `translateX(-${activeIndex * 100}%)` }">
          <div class="schedules-page__pane" :inert="activeTab !== 'routes'">
            <RoutesList />
          </div>
          <div class="schedules-page__pane" :inert="activeTab !== 'stops'">
            <StopsList />
          </div>
        </div>
      </div>
    </div>

    <FavoritesOnboardingSheet v-if="showFavoritesOnboarding" @close="showFavoritesOnboarding = false" />
  </div>
</template>

<style lang="scss">
.schedules-page {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    color: $color-neutral-highest;
    // Same ad-hoc large title as the stop sheet: the design system has no
    // heading token this size yet.
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    margin: 0;
    padding: $spacing-4 $spacing-5;
  }

  &__tabs {
    flex-shrink: 0;

    .tabs-bar__tab {
      flex: 1 1 0;
    }
  }

  &__viewport {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  &__track {
    display: flex;
    height: 100%;
    transition: transform $duration-drawer-open $easing-drawer;
    will-change: transform;
  }

  &__pane {
    flex: 0 0 100%;
    overflow-y: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .schedules-page__track {
    transition: none;
  }
}
</style>
